import { Program } from "@coral-xyz/anchor";
import {
  ComputeBudgetProgram,
  Connection,
  Keypair,
  PublicKey,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import {
  Mint,
  unpackMint,
  createAssociatedTokenAccountIdempotentInstruction,
  getAssociatedTokenAddressSync,
} from "@solana/spl-token";
import BN from "bn.js";
import { REFERRAL_PROGRAM_ID } from ".";
import type { Referral as ReferralTypes } from "./idl/referral";
import ReferralIDL from "./idl/referral.json";
import {
  EscrowAccount,
  ReferralProgram,
  StakingAccount,
  UnstakeAccount,
  ConfigFields,
} from "./type";
import { StakingWrapper } from "./accounts/staking_wrapper";
import { EscrowWrapper } from "./accounts/escrow_wrapper";
import { UnstakeWrapper } from "./accounts/unstake_wrapper";
import {
  getEscrowFilter,
  getEscrowStakingFilter,
  getEscrowOwnerFilter,
  getUnstakeFilter,
  getUnstakeOwnerFilter,
  getUnstakeEscrowFilter,
} from "./accounts";
import { deriveEscrow } from "./pda";
import { getSimulationComputeUnits } from "./rpc";
import {
  createInitializeStakingIx,
  IInitializeStakingParams,
  createInitializeEscrowIx,
  createStakeIx,
  createImmediateUnstakeIx,
  createUnstakeIx,
  createCancelUnstakeIx,
  createWithdrawIx,
  createCloseEscrowIx,
  createClaimPenaltyFeeIx,
  createUpdatePenaltyFeeReceiverIx,
  createTransferOwnerIx,
  createUpdateConfigIx,
} from "./instructions";

function createReferralProgram(
  connection: Connection,
  programId: PublicKey,
): ReferralProgram {
  return new Program<ReferralTypes>(
    {
      ...ReferralIDL,
      address: programId.toBase58(),
    } as ReferralTypes,
    {
      connection,
    },
  );
}

async function fetchAccountsForCache(
  referralProgram: ReferralProgram,
  stakingAddress: PublicKey,
): Promise<{
  stakingAccount: StakingAccount;
  tokenDecimals: number;
}> {
  const connection = referralProgram.provider.connection;

  const stakingAccount =
    await referralProgram.account.staking.fetch(stakingAddress);

  const mintAccount = await connection.getAccountInfo(stakingAccount.tokenMint);
  if (!mintAccount) {
    throw new Error("Failed to fetch mint account");
  }

  const mint = unpackMint(
    stakingAccount.tokenMint,
    mintAccount,
    mintAccount.owner,
  );

  return {
    stakingAccount,
    tokenDecimals: mint.decimals,
  };
}

async function buildTransactionWithOptionalComputeUnitOptimization(
  connection: Connection,
  instructions: TransactionInstruction[],
  feePayer: PublicKey,
  optimizeCu: boolean,
) {
  if (optimizeCu) {
    const estimatedComputeUnit = await getSimulationComputeUnits(
      connection,
      instructions,
      feePayer,
      [],
    ).catch((_e) => {
      return Math.min(1_400_000, instructions.length * 200_000);
    });

    const setCuIx = ComputeBudgetProgram.setComputeUnitLimit({
      units: Math.min((estimatedComputeUnit ?? 200_000) * 1.1, 1_400_000),
    });

    return buildTransaction(connection, [setCuIx, ...instructions], feePayer);
  } else {
    return buildTransaction(connection, instructions, feePayer);
  }
}

async function buildTransaction(
  connection: Connection,
  instructions: TransactionInstruction[],
  feePayer: PublicKey,
) {
  const latestBlockhashInfo = await connection.getLatestBlockhash();
  return new Transaction({
    feePayer,
    ...latestBlockhashInfo,
  }).add(...instructions);
}

export class Referral {
  constructor(
    public program: ReferralProgram,
    public stakingAddress: PublicKey,
    public stakingAccount: StakingAccount,
    public tokenDecimals: number,
    public shouldOptimizeComputeUnit: boolean,
  ) {}

  static async create(
    connection: Connection,
    stakingAddress: PublicKey,
    programId?: PublicKey,
  ): Promise<Referral> {
    const referralProgram = createReferralProgram(
      connection,
      programId ?? REFERRAL_PROGRAM_ID,
    );

    const { stakingAccount, tokenDecimals } = await fetchAccountsForCache(
      referralProgram,
      stakingAddress,
    );

    return new Referral(
      referralProgram,
      stakingAddress,
      stakingAccount,
      tokenDecimals,
      true,
    );
  }

  /** Permissionless: Anyone can initialize a new staking pool. Signers: staking, payer. */
  static async initializeStaking(
    connection: Connection,
    params: Omit<IInitializeStakingParams, "referralProgram">,
    programId?: PublicKey,
    shouldOptimizeComputeUnit?: boolean,
  ): Promise<Transaction> {
    const referralProgram = createReferralProgram(
      connection,
      programId ?? REFERRAL_PROGRAM_ID,
    );

    const ix = await createInitializeStakingIx({
      referralProgram,
      ...params,
    });

    return buildTransactionWithOptionalComputeUnitOptimization(
      connection,
      [ix],
      params.payer,
      shouldOptimizeComputeUnit ?? true,
    );
  }

  private get connection(): Connection {
    return this.program.provider.connection;
  }

  setComputeUnitOptimization(enable: boolean): void {
    this.shouldOptimizeComputeUnit = enable;
  }

  async refetchState(): Promise<void> {
    const { stakingAccount, tokenDecimals } = await fetchAccountsForCache(
      this.program,
      this.stakingAddress,
    );
    this.stakingAccount = stakingAccount;
    this.tokenDecimals = tokenDecimals;
  }

  getStakingWrapper(): StakingWrapper {
    return new StakingWrapper(this.stakingAccount, this.tokenDecimals);
  }

  /** Permissionless: Anyone can initialize an escrow for any owner. Signer: payer. */
  async initializeEscrow(params: {
    owner: PublicKey;
    payer: PublicKey;
  }): Promise<Transaction> {
    const ix = await createInitializeEscrowIx({
      referralProgram: this.program,
      stakingAddress: this.stakingAddress,
      ...params,
    });

    return buildTransactionWithOptionalComputeUnitOptimization(
      this.connection,
      [ix],
      params.payer,
      this.shouldOptimizeComputeUnit,
    );
  }

  /** Escrow-owner-gated: Only the escrow owner can stake into their escrow. Signer: owner. */
  async stake(params: {
    escrowAddress: PublicKey;
    owner: PublicKey;
    amount: BN;
  }): Promise<Transaction> {
    const ix = await createStakeIx({
      referralProgram: this.program,
      stakingAddress: this.stakingAddress,
      stakingAccount: this.stakingAccount,
      ...params,
    });

    return buildTransactionWithOptionalComputeUnitOptimization(
      this.connection,
      [ix],
      params.owner,
      this.shouldOptimizeComputeUnit,
    );
  }

  /** Permissioned: Only the escrow owner can immediate unstake. Signer: owner. */
  async immediateUnstake(params: {
    escrowAddress: PublicKey;
    owner: PublicKey;
    maxAmount: BN;
    maxImmediateUnstakePenaltyBps: number;
    receiver: PublicKey;
  }): Promise<Transaction> {
    const ix = await createImmediateUnstakeIx({
      referralProgram: this.program,
      stakingAddress: this.stakingAddress,
      stakingAccount: this.stakingAccount,
      ...params,
    });

    const createAtaIx = createAssociatedTokenAccountIdempotentInstruction(
      params.owner,
      getAssociatedTokenAddressSync(
        this.stakingAccount.tokenMint,
        params.receiver,
        true,
      ),
      params.receiver,
      this.stakingAccount.tokenMint,
    );

    return buildTransactionWithOptionalComputeUnitOptimization(
      this.connection,
      [createAtaIx, ix],
      params.owner,
      this.shouldOptimizeComputeUnit,
    );
  }

  /** Permissioned: Only the escrow owner can unstake. Signers: owner, payer, unstakeKeypair. */
  async unstake(params: {
    escrowAddress: PublicKey;
    owner: PublicKey;
    payer: PublicKey;
    maxAmount: BN;
    unstakeKeypair?: Keypair;
  }): Promise<{ transaction: Transaction; unstakeKeypair: Keypair }> {
    const { instruction, unstakeKeypair } = await createUnstakeIx({
      referralProgram: this.program,
      stakingAddress: this.stakingAddress,
      ...params,
    });

    const transaction =
      await buildTransactionWithOptionalComputeUnitOptimization(
        this.connection,
        [instruction],
        params.payer,
        this.shouldOptimizeComputeUnit,
      );

    return { transaction, unstakeKeypair };
  }

  /** Permissioned: Only the unstake owner can cancel an unstake. Signer: owner. */
  async cancelUnstake(params: {
    unstakeAddress: PublicKey;
    escrowAddress: PublicKey;
    owner: PublicKey;
    rentReceiver: PublicKey;
  }): Promise<Transaction> {
    const ix = await createCancelUnstakeIx({
      referralProgram: this.program,
      stakingAddress: this.stakingAddress,
      ...params,
    });

    return buildTransactionWithOptionalComputeUnitOptimization(
      this.connection,
      [ix],
      params.owner,
      this.shouldOptimizeComputeUnit,
    );
  }

  /** Permissioned: Only the unstake owner can withdraw after cooldown. Signer: owner. */
  async withdraw(params: {
    unstakeAddress: PublicKey;
    escrowAddress: PublicKey;
    owner: PublicKey;
    rentReceiver: PublicKey;
    receiver: PublicKey;
  }): Promise<Transaction> {
    const ix = await createWithdrawIx({
      referralProgram: this.program,
      stakingAddress: this.stakingAddress,
      stakingAccount: this.stakingAccount,
      ...params,
    });

    const createAtaIx = createAssociatedTokenAccountIdempotentInstruction(
      params.owner,
      getAssociatedTokenAddressSync(
        this.stakingAccount.tokenMint,
        params.receiver,
        true,
      ),
      params.receiver,
      this.stakingAccount.tokenMint,
    );

    return buildTransactionWithOptionalComputeUnitOptimization(
      this.connection,
      [createAtaIx, ix],
      params.owner,
      this.shouldOptimizeComputeUnit,
    );
  }

  /** Permissioned: Only the escrow owner can close the escrow. Signer: owner. */
  async closeEscrow(params: {
    escrowAddress: PublicKey;
    owner: PublicKey;
    rentReceiver: PublicKey;
    skipCloseIfNonEmpty: boolean;
  }): Promise<Transaction> {
    const ix = await createCloseEscrowIx({
      referralProgram: this.program,
      stakingAddress: this.stakingAddress,
      ...params,
    });

    return buildTransactionWithOptionalComputeUnitOptimization(
      this.connection,
      [ix],
      params.owner,
      this.shouldOptimizeComputeUnit,
    );
  }

  /** Permissionless: Anyone can trigger penalty fee claim. Tokens are sent to the configured fee receiver. */
  async claimPenaltyFee(params: { maxAmount: BN }): Promise<Transaction> {
    const ix = await createClaimPenaltyFeeIx({
      referralProgram: this.program,
      stakingAddress: this.stakingAddress,
      stakingAccount: this.stakingAccount,
      ...params,
    });

    const feeReceiver = this.stakingAccount.config.feeReceiver;
    const createAtaIx = createAssociatedTokenAccountIdempotentInstruction(
      feeReceiver,
      getAssociatedTokenAddressSync(
        this.stakingAccount.tokenMint,
        feeReceiver,
        true,
      ),
      feeReceiver,
      this.stakingAccount.tokenMint,
    );

    return buildTransactionWithOptionalComputeUnitOptimization(
      this.connection,
      [createAtaIx, ix],
      feeReceiver,
      this.shouldOptimizeComputeUnit,
    );
  }

  /** Permissioned: Only the staking owner can update the penalty fee receiver. Signer: owner. */
  async updatePenaltyFeeReceiver(params: {
    owner: PublicKey;
    newFeeReceiver: PublicKey;
  }): Promise<Transaction> {
    const ix = await createUpdatePenaltyFeeReceiverIx({
      referralProgram: this.program,
      stakingAddress: this.stakingAddress,
      ...params,
    });

    return buildTransactionWithOptionalComputeUnitOptimization(
      this.connection,
      [ix],
      params.owner,
      this.shouldOptimizeComputeUnit,
    );
  }

  /** Permissioned: Only the staking owner can transfer ownership. Signer: owner. */
  async transferOwner(params: {
    owner: PublicKey;
    newOwner: PublicKey;
  }): Promise<Transaction> {
    const ix = await createTransferOwnerIx({
      referralProgram: this.program,
      stakingAddress: this.stakingAddress,
      ...params,
    });

    return buildTransactionWithOptionalComputeUnitOptimization(
      this.connection,
      [ix],
      params.owner,
      this.shouldOptimizeComputeUnit,
    );
  }

  /** Permissioned: Only the staking owner can update config. Signer: owner. */
  async updateConfig(params: {
    owner: PublicKey;
    fieldsToUpdate: ConfigFields[];
  }): Promise<Transaction> {
    const ix = await createUpdateConfigIx({
      referralProgram: this.program,
      stakingAddress: this.stakingAddress,
      ...params,
    });

    return buildTransactionWithOptionalComputeUnitOptimization(
      this.connection,
      [ix],
      params.owner,
      this.shouldOptimizeComputeUnit,
    );
  }

  // Fetch helpers

  async fetchEscrow(owner: PublicKey): Promise<EscrowWrapper | null> {
    const escrowAddress = deriveEscrow(
      this.stakingAddress,
      owner,
      this.program.programId,
    );

    try {
      const account = await this.program.account.escrow.fetch(escrowAddress);
      return new EscrowWrapper(account, this.tokenDecimals);
    } catch {
      return null;
    }
  }

  async fetchAllEscrows(): Promise<
    { pubkey: PublicKey; account: EscrowWrapper }[]
  > {
    const accounts = await this.program.account.escrow.all([
      getEscrowStakingFilter(this.stakingAddress),
    ]);

    return accounts.map((a) => ({
      pubkey: a.publicKey,
      account: new EscrowWrapper(a.account, this.tokenDecimals),
    }));
  }

  async fetchUnstakesByOwner(
    owner: PublicKey,
  ): Promise<{ pubkey: PublicKey; account: UnstakeWrapper }[]> {
    const accounts = await this.program.account.unstake.all([
      getUnstakeOwnerFilter(owner),
    ]);

    return accounts.map((a) => ({
      pubkey: a.publicKey,
      account: new UnstakeWrapper(a.account, this.tokenDecimals),
    }));
  }

  async fetchUnstakesByOwnerAndEscrow(
    owner: PublicKey,
    escrow: PublicKey,
  ): Promise<{ pubkey: PublicKey; account: UnstakeWrapper }[]> {
    const accounts = await this.program.account.unstake.all([
      getUnstakeOwnerFilter(owner),
      getUnstakeEscrowFilter(escrow),
    ]);

    return accounts.map((a) => ({
      pubkey: a.publicKey,
      account: new UnstakeWrapper(a.account, this.tokenDecimals),
    }));
  }
}
